import {
    Bot24Regular,
    ChevronDown20Regular,
    Food24Regular,
    Gift24Regular,
    Lightbulb24Regular,
    People24Regular,
    Settings24Regular,
    ShoppingBag24Regular,
    WalletCreditCard24Regular
} from "@fluentui/react-icons";
import {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import {createPortal} from "react-dom";
import {useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {AppState} from "@/state/rootReducer";
import {MonitorUrlModel} from "@/util/appParamsUtil";
import {getCurrentSelectedBusinessUnitId} from "@/util/stateUtil";
import {useBentoIntelligence} from "../BentoIntelligenceContext";
import {useBentoPersona} from "../BentoPersonaContext";
import {useBentoViewMode} from "../BentoViewModeContext";
import {BENTO_PERSONAS, BentoPersonaId} from "../bentoPersona";

let bentoPagesPrefetched = false;

const prefetchBentoPages = (): void => {
    if (bentoPagesPrefetched) {
        return;
    }
    bentoPagesPrefetched = true;

    void import("../pages/coaches/BentoCoaches");
    void import("../pages/marketplace/BentoMarketplace");
    void import("../pages/guest360/BentoGuest360");
    void import("../pages/back-of-house/BentoBackOfHouse");
    void import("../pages/pos/BentoPos");
    void import("../pages/ordering/BentoOrdering");
    void import("../pages/loyalty/BentoLoyalty");
    void import("../pages/reporting/BentoReporting");
    void import("../pages/coach/BentoCoach");
    void import("../pages/checklist/BentoChecklist");
};

interface NavItem {
    id: string;
    label: string;
    path?: string;
    routeId?: string;
}

interface NavCategory {
    id: string;
    label: string;
    icon: ReactNode;
    items: NavItem[];
}

const navCategories: NavCategory[] = [
    {
        id: "sell",
        label: "Sell",
        icon: <Food24Regular />,
        items: [
            {id: "kiosk", label: "Kiosk"},
            {id: "ordering", label: "Ordering", routeId: "ordering"},
            {id: "driveThru", label: "Drive-Thru"},
            {id: "digitalExperience", label: "Digital Experience"},
            {id: "pay", label: "Pay"},
            {id: "register", label: "Register", path: "/par-intelligence/pos"},
            {
                id: "thirdPartyOrders",
                label: "3rd Party Orders"
            }
        ]
    },
    {
        id: "engage",
        label: "Engage",
        icon: <Gift24Regular />,
        items: [
            {id: "loyalty", label: "Loyalty", routeId: "loyalty"},
            {id: "campaigns", label: "Campaigns", path: "/par-intelligence/loyalty"},
            {id: "offers", label: "Offers", path: "/par-intelligence/loyalty"},
            {id: "guest360", label: "Guest360", routeId: "quest360"},
            {id: "accelerate", label: "Accelerate"}
        ]
    },
    {
        id: "operate",
        label: "Operate",
        icon: <People24Regular />,
        items: [
            {id: "labor", label: "Labor", path: "/par-intelligence/backofhouse"},
            {
                id: "inventory",
                label: "Inventory",
                path: "/par-intelligence/backofhouse"
            },
            {
                id: "kitchenKds",
                label: "Kitchen / KDS"
            },
            {
                id: "checklists",
                label: "Checklists",
                path: "/par-intelligence/checklist"
            }
        ]
    },
    {
        id: "restaurantCore",
        label: "Restaurant Core",
        icon: <WalletCreditCard24Regular />,
        items: [
            {id: "menuMgmt", label: "Menu Mgmt", path: "/par-intelligence/pos"},
            {id: "reportingHub", label: "Reporting Hub", routeId: "reporting"},
            {id: "parIntelligence", label: "PAR Intelligence", path: "/"}
        ]
    }
];

const NAVIGABLE_IDS = new Set([
    "marketplace",
    "coach",
    "detect",
    "quest360",
    "backOfHouse",
    "pos",
    "ordering",
    "loyalty",
    "reporting"
]);

interface SettingToggle {
    id: string;
    label: string;
    description: string;
    defaultOn: boolean;
}

const settingToggles: SettingToggle[] = [
    {
        id: "emailNotifications",
        label: "Email notifications",
        description: "Receive a daily digest of product alerts.",
        defaultOn: true
    },
    {
        id: "compactLabels",
        label: "Compact navigation",
        description: "Show smaller labels in the sidebar.",
        defaultOn: false
    },
    {
        id: "productStatus",
        label: "Active products indicator",
        description: "Display the status dot in the footer.",
        defaultOn: true
    }
];

const BentoDemoSidebar = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const {persona, setPersonaId} = useBentoPersona();
    const clientConcept = useSelector(
        (state: AppState) => state.application.selectedClientConcept
    );
    const businessUnitId = useSelector((state: AppState) =>
        getCurrentSelectedBusinessUnitId(state)
    );

    const {mode, setMode} = useBentoIntelligence();
    const {mode: viewMode, setMode: setViewMode} = useBentoViewMode();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [panelPos, setPanelPos] = useState<{left: number; bottom: number} | null>(
        null
    );
    const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
    const [toggleState, setToggleState] = useState<Record<string, boolean>>(() =>
        settingToggles.reduce<Record<string, boolean>>((acc, toggle) => {
            acc[toggle.id] = toggle.defaultOn;
            return acc;
        }, {})
    );
    const triggerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        prefetchBentoPages();
    }, []);

    useLayoutEffect(() => {
        if (!settingsOpen || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPanelPos({
            left: rect.right + 8,
            bottom: window.innerHeight - rect.bottom
        });
    }, [settingsOpen]);

    useEffect(() => {
        if (!settingsOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current && triggerRef.current.contains(target)) {
                return;
            }
            if (panelRef.current && panelRef.current.contains(target)) {
                return;
            }
            setSettingsOpen(false);
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setSettingsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [settingsOpen]);

    const isCoachesActive = location.pathname.startsWith(
        "/par-intelligence/coaches"
    );
    const isMarketplaceActive = location.pathname.startsWith(
        "/par-intelligence/marketplace"
    );
    // Exact match (not startsWith) so this doesn't also match /par-intelligence/coaches.
    const isCoachActive = location.pathname === "/par-intelligence/coach";
    const isDetectActive = location.pathname.startsWith("/dashboard/monitors");
    const isGuest360Active = location.pathname.startsWith(
        "/par-intelligence/guest360"
    );
    const isBackOfHouseActive = location.pathname.startsWith(
        "/par-intelligence/backofhouse"
    );
    const isPosActive = location.pathname.startsWith("/par-intelligence/pos");
    const isOrderingActive = location.pathname.startsWith(
        "/par-intelligence/ordering"
    );
    const isLoyaltyActive = location.pathname.startsWith(
        "/par-intelligence/loyalty"
    );
    const isReportingActive = location.pathname.startsWith(
        "/par-intelligence/reporting"
    );
    const isBentoActive =
        location.pathname === "/" ||
        (location.pathname.startsWith("/par-intelligence") &&
            !isCoachesActive &&
            !isMarketplaceActive &&
            !isGuest360Active &&
            !isBackOfHouseActive &&
            !isPosActive &&
            !isOrderingActive &&
            !isLoyaltyActive &&
            !isReportingActive &&
            !isCoachActive);

    const isInsightsActive = isBentoActive && !openCategoryId;
    const isRailExpanded = isHovered || settingsOpen;

    const handleItemClick = useCallback(
        (id: string) => {
            if (id === "marketplace") {
                history.push("/par-intelligence/marketplace");
            } else if (id === "coach") {
                history.push("/par-intelligence/coach");
            } else if (id === "detect") {
                if (clientConcept?.conceptUrlName && businessUnitId) {
                    const url = new MonitorUrlModel(
                        clientConcept.conceptUrlName,
                        businessUnitId
                    );
                    history.push(url.url);
                } else {
                    history.push("/dashboard/monitors");
                }
            } else if (id === "quest360") {
                history.push("/par-intelligence/guest360");
            } else if (id === "backOfHouse") {
                history.push("/par-intelligence/backofhouse");
            } else if (id === "pos") {
                history.push("/par-intelligence/pos");
            } else if (id === "ordering") {
                history.push("/par-intelligence/ordering");
            } else if (id === "loyalty") {
                history.push("/par-intelligence/loyalty");
            } else if (id === "reporting") {
                history.push("/par-intelligence/reporting");
            }
        },
        [history, clientConcept, businessUnitId]
    );

    const getRouteActivity = (id: string): boolean => {
        if (id === "marketplace") return isMarketplaceActive;
        if (id === "coach") return isCoachActive;
        if (id === "detect") return isDetectActive;
        if (id === "quest360") return isGuest360Active;
        if (id === "backOfHouse") return isBackOfHouseActive;
        if (id === "pos") return isPosActive;
        if (id === "ordering") return isOrderingActive;
        if (id === "loyalty") return isLoyaltyActive;
        if (id === "reporting") return isReportingActive;
        return false;
    };

    const handleProductClick = (item: NavItem): void => {
        if (item.routeId && NAVIGABLE_IDS.has(item.routeId)) {
            handleItemClick(item.routeId);
            return;
        }
        if (item.path) {
            history.push(item.path);
        }
    };

    const isProductRouteActive = (item: NavItem): boolean => {
        if (item.routeId) {
            return getRouteActivity(item.routeId);
        }

        if (item.path === "/") {
            return false;
        }

        return Boolean(item.path && location.pathname.startsWith(item.path));
    };

    const activeCategoryId =
        navCategories.find(
            (category) =>
                category.id === openCategoryId &&
                category.items.some((item) => isProductRouteActive(item))
        )?.id ??
        navCategories.find((category) =>
            category.items.some((item) => isProductRouteActive(item))
        )?.id;

    return (
        <nav
            className={`bento-demo-sidebar${
                isRailExpanded ? " bento-demo-sidebar--expanded" : ""
            }`}
            aria-label="PAR Intelligence navigation"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="bento-demo-sidebar__rail">
                <ul className="bento-demo-sidebar__nav">
                    <li>
                        <button
                            type="button"
                            className={`bento-demo-sidebar__item${
                                isInsightsActive
                                    ? " bento-demo-sidebar__item--active"
                                    : ""
                            }`}
                            aria-current={isInsightsActive ? "page" : undefined}
                            title="Insights"
                            onClick={() => {
                                setOpenCategoryId("");
                                history.push("/");
                            }}
                        >
                            <span className="bento-demo-sidebar__icon">
                                <Lightbulb24Regular />
                            </span>
                            <span className="bento-demo-sidebar__label">
                                Insights
                            </span>
                        </button>
                    </li>

                    {navCategories.map((category) => {
                        const isSelected = openCategoryId === category.id;
                        const activeProductId = category.items.find((item) =>
                            isProductRouteActive(item)
                        )?.id;
                        return (
                            <li
                                key={category.id}
                                className="bento-demo-sidebar__section"
                            >
                                <button
                                    type="button"
                                    className={`bento-demo-sidebar__item bento-demo-sidebar__item--category${
                                        activeCategoryId === category.id
                                            ? " bento-demo-sidebar__item--active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setOpenCategoryId((current) =>
                                            current === category.id
                                                ? null
                                                : category.id
                                        )
                                    }
                                    title={category.label}
                                    aria-expanded={isSelected}
                                >
                                    <span className="bento-demo-sidebar__icon">
                                        {category.icon}
                                    </span>
                                    <span className="bento-demo-sidebar__label">
                                        {category.label}
                                    </span>
                                    <span className="bento-demo-sidebar__chevron">
                                        <ChevronDown20Regular />
                                    </span>
                                </button>

                                {isSelected && (
                                    <ul className="bento-demo-sidebar__products">
                                        {category.items.map((item) => {
                                            const itemIsActive =
                                                activeProductId === item.id;
                                            const isNavigable =
                                                Boolean(item.path) ||
                                                Boolean(
                                                    item.routeId &&
                                                        NAVIGABLE_IDS.has(
                                                            item.routeId
                                                        )
                                                );

                                            return (
                                                <li key={item.id}>
                                                    <button
                                                        type="button"
                                                        className={`bento-demo-sidebar__product${
                                                            itemIsActive
                                                                ? " bento-demo-sidebar__product--active"
                                                                : ""
                                                        }${
                                                            !isNavigable
                                                                ? " bento-demo-sidebar__product--stub"
                                                                : ""
                                                        }`}
                                                        onClick={() =>
                                                            handleProductClick(item)
                                                        }
                                                        disabled={!isNavigable}
                                                        title={item.label}
                                                    >
                                                        {item.label}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>

                <div className="bento-demo-sidebar__footer">
                    <button
                        type="button"
                        className={`bento-demo-sidebar__item${
                            isCoachesActive ? " bento-demo-sidebar__item--active" : ""
                        }`}
                        aria-current={isCoachesActive ? "page" : undefined}
                        title="Coaches"
                        onClick={() => history.push("/par-intelligence/coaches")}
                    >
                        <span className="bento-demo-sidebar__icon">
                            <Bot24Regular />
                        </span>
                        <span className="bento-demo-sidebar__label">Coaches</span>
                    </button>
                    <button
                        type="button"
                        className={`bento-demo-sidebar__item${
                            isMarketplaceActive
                                ? " bento-demo-sidebar__item--active"
                                : ""
                        }`}
                        aria-current={isMarketplaceActive ? "page" : undefined}
                        title="PAR Marketplace"
                        onClick={() => handleItemClick("marketplace")}
                    >
                        <span className="bento-demo-sidebar__icon">
                            <ShoppingBag24Regular />
                        </span>
                        <span className="bento-demo-sidebar__label">
                            PAR Marketplace
                        </span>
                    </button>
                    <div className="bento-demo-sidebar__settings" ref={triggerRef}>
                        <button
                            type="button"
                            className={`bento-demo-sidebar__item${
                                settingsOpen
                                    ? " bento-demo-sidebar__item--active"
                                    : ""
                            }`}
                            title="Settings"
                            aria-haspopup="dialog"
                            aria-expanded={settingsOpen}
                            onClick={() => setSettingsOpen((open) => !open)}
                        >
                            <span className="bento-demo-sidebar__icon">
                                <Settings24Regular />
                            </span>
                            <span className="bento-demo-sidebar__label">
                                Settings
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {settingsOpen &&
                panelPos &&
                createPortal(
                    <div
                        ref={panelRef}
                        className="bento-demo-sidebar__settings-panel"
                        role="dialog"
                        aria-label="Settings"
                        style={{left: panelPos.left, bottom: panelPos.bottom}}
                    >
                        <div className="bento-demo-sidebar__settings-header">
                            <span className="bento-demo-sidebar__settings-title">
                                Settings
                            </span>
                            <span className="bento-demo-sidebar__settings-subtitle">
                                Personalize your PAR Intelligence workspace.
                            </span>
                        </div>

                        <ul className="bento-demo-sidebar__settings-list">
                            <li className="bento-demo-sidebar__settings-row bento-demo-sidebar__settings-row--stacked">
                                <span className="bento-demo-sidebar__settings-text">
                                    <span className="bento-demo-sidebar__settings-label">
                                        Viewing as
                                    </span>
                                    <span className="bento-demo-sidebar__settings-desc">
                                        Preview the insights workspace for a specific
                                        role.
                                    </span>
                                </span>
                                <select
                                    className="bento-demo-sidebar__persona-select"
                                    value={persona.id}
                                    onChange={(event) =>
                                        setPersonaId(
                                            event.target.value as BentoPersonaId
                                        )
                                    }
                                    style={
                                        {
                                            "--par-persona-accent": persona.accent
                                        } as React.CSSProperties
                                    }
                                >
                                    {BENTO_PERSONAS.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.title}
                                        </option>
                                    ))}
                                </select>
                            </li>
                            <li className="bento-demo-sidebar__settings-row">
                                <span className="bento-demo-sidebar__settings-text">
                                    <span className="bento-demo-sidebar__settings-label">
                                        Live PAR Intelligence
                                    </span>
                                    <span className="bento-demo-sidebar__settings-desc">
                                        Use real PAR Intelligence answers instead of
                                        demo responses.
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={mode === "live"}
                                    aria-label="Live PAR Intelligence"
                                    className={`bento-demo-sidebar__switch${
                                        mode === "live"
                                            ? " bento-demo-sidebar__switch--on"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setMode(mode === "live" ? "demo" : "live")
                                    }
                                >
                                    <span className="bento-demo-sidebar__switch-thumb" />
                                </button>
                            </li>
                            <li className="bento-demo-sidebar__settings-row">
                                <span className="bento-demo-sidebar__settings-text">
                                    <span className="bento-demo-sidebar__settings-label">
                                        Skills-forward view
                                    </span>
                                    <span className="bento-demo-sidebar__settings-desc">
                                        Show the home screen as individual skill
                                        cards instead of grouped by coach.
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={viewMode === "skills"}
                                    aria-label="Skills-forward view"
                                    className={`bento-demo-sidebar__switch${
                                        viewMode === "skills"
                                            ? " bento-demo-sidebar__switch--on"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setViewMode(
                                            viewMode === "skills"
                                                ? "coaches"
                                                : "skills"
                                        )
                                    }
                                >
                                    <span className="bento-demo-sidebar__switch-thumb" />
                                </button>
                            </li>
                            {settingToggles.map((toggle) => (
                                <li
                                    key={toggle.id}
                                    className="bento-demo-sidebar__settings-row"
                                >
                                    <span className="bento-demo-sidebar__settings-text">
                                        <span className="bento-demo-sidebar__settings-label">
                                            {toggle.label}
                                        </span>
                                        <span className="bento-demo-sidebar__settings-desc">
                                            {toggle.description}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={toggleState[toggle.id]}
                                        aria-label={toggle.label}
                                        className={`bento-demo-sidebar__switch${
                                            toggleState[toggle.id]
                                                ? " bento-demo-sidebar__switch--on"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            setToggleState((prev) => ({
                                                ...prev,
                                                [toggle.id]: !prev[toggle.id]
                                            }))
                                        }
                                    >
                                        <span className="bento-demo-sidebar__switch-thumb" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body
                )}
        </nav>
    );
};

export default BentoDemoSidebar;
